import { View, Text } from "react-native";
import { Button, ButtonIcon, ButtonText } from "ui/gluestack/button";
import { AddIcon, Icon, TrashIcon } from "ui/gluestack/icon";
import { FormControlledInputTextArea } from "ui/molecules/form/FormControlledInputTextArea";
import {
  requiredValidation,
  validateFileExtensions,
} from "ui/utils/Validators";
import { useFieldArray, Control, Controller } from "react-hook-form";
import { AddHealthConditionsForm } from "app/services/api/types/residents/models/AddHealthConditionsForm";
import UploadDocumentArea from "ui/organisms/UploadDocumentArea";
import { generalAllowedExtensions } from "app/constants/allowedExtensionsConstants";

interface Props {
  control: Control<AddHealthConditionsForm>;
  errorMessage: string | null;
}

export default function MedicalHistoryItem({ control, errorMessage }: Props) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "health_conditions",
  });

  return (
    <View>
      {fields.map((item, index) => (
        <View
          key={item.id}
          className="flex flex-row w-full md:w-9/12 gap-x-4 mb-7"
        >
          <Text>{index + 1}. </Text>
          <View className="md:w-10/12 flex flex-1">
            <FormControlledInputTextArea
              id={`health_conditions.${index}.description`}
              name={`health_conditions.${index}.description`}
              control={control}
              placeholder="Enter any resident’s health condition/medical history"
              isRequired
              className="text-gray-500"
              rules={{
                required:
                  index !== 0
                    ? requiredValidation("Resident's medical history")
                    : undefined,
                validate: {
                  requiredIf: (value, formValues) => {
                    if (
                      !value &&
                      formValues.health_conditions[index]
                        ?.health_condition_media?.length
                    ) {
                      return "Resident's medical history is required";
                    }
                    return true;
                  },
                },
              }}
            />

            <Controller
              control={control}
              name={`health_conditions.${index}.health_condition_media`}
              defaultValue={[]}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <UploadDocumentArea
                  value={value}
                  onChange={onChange}
                  allowedExtensions={generalAllowedExtensions}
                  errorMessage={error}
                  singleFile
                />
              )}
              rules={{
                validate: validateFileExtensions(generalAllowedExtensions),
              }}
            />

            {index > 0 ? (
              <View className="self-end">
                <Button
                  size="xs"
                  action="negative"
                  variant="outline"
                  onPress={() => remove(index)}
                >
                  <ButtonText>Remove record</ButtonText>
                  <ButtonIcon as={TrashIcon} />
                </Button>
              </View>
            ) : null}
          </View>
        </View>
      ))}

      <View className="w-fit">
        <Button
          className="bg-secondary-0 text-nowrap"
          onPress={() =>
            append({ description: "", health_condition_media: [] })
          }
          action="secondary"
        >
          <Text className="text-typography-900">Add new condition/history</Text>
          <Icon as={AddIcon} className="w-4 h-4" />
        </Button>
      </View>

      {errorMessage ? (
        <Text className="text-red-700">{errorMessage}</Text>
      ) : null}
    </View>
  );
}
